import { Ref, useEffect, useEffectEvent, useState } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  ItemNoAndTypeWithPieces,
  ItemNoAndTypeWithPTagsOrdered,
} from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  isLoading,
  Loading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import LoadingCreatorFragment from "../InvalidCreatorFragment/InvalidCreatorFragment";
import { ListItem } from "../../../SelectListItem/SelectListItem";
import { ArticlesPrintPtagsQuantity } from "../../../ArticlesPrintPtagsQuantity/ArticlesPrintPtagsQuantity";

interface VisibilityCondition {
  field: string;
  value: any;
  operator: "in";
}

export interface ArticlesPrintPtagsCreatorFragmentConfigProps {
  title: string;
  field: string;
  visibility: VisibilityCondition;
}

interface ArticlesPrintPtagsCreatorFragmentProps {
  configProps: ArticlesPrintPtagsCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
}

function shouldShowField(
  task: CreatorFragmentCommonProps["task"],
  visibility: VisibilityCondition
): boolean {
  const currentValue = getByPath(task, visibility.field) as ListItem | null;

  if (visibility.operator === "in") {
    return Array.isArray(visibility.value)
      ? visibility.value.includes(currentValue?.value)
      : false;
  }

  return true;
}

const ArticlesPrintPtagsCreatorFragment = ({
  configProps,
  commonProps,
}: ArticlesPrintPtagsCreatorFragmentProps) => {
  // Component logic here

  const { field, visibility } = configProps;
  const { task, taskType, onTaskChange } = commonProps;

  const itemNosAndTypeWithPieces = validateInitialDetailsFieldValue<
    ItemNoAndTypeWithPieces[]
  >(getByPath(task, field), field, taskType);

  const initializeItemsOrderedDetails = useEffectEvent(() => {
    if (
      !Array.isArray(itemNosAndTypeWithPieces) ||
      isLoading(itemNosAndTypeWithPieces)
    ) {
      return;
    }

    const hasOrderedFields = itemNosAndTypeWithPieces.some(
      (item) => "pieces_ordered" in item || "ptags_ordered" in item
    );

    if (!hasOrderedFields) {
      const itemsWithOrderedDetails = itemNosAndTypeWithPieces.map((item) => ({
        ...item,
        pieces_ordered: item.pieces,
        ptags_ordered: item.pieces,
      }));

      onTaskChange({ details: { items: itemsWithOrderedDetails } });
    }
  });

  useEffect(() => {
    initializeItemsOrderedDetails();
  }, []);

  const initialItemNosAndTypeWithOrderedDetails:
    | ItemNoAndTypeWithPTagsOrdered[]
    | null = Array.isArray(itemNosAndTypeWithPieces)
    ? itemNosAndTypeWithPieces.map((item) => ({
        ...item,
        ptags_ordered: item.pieces,
      }))
    : null;

  const [
    itemNosAndTypeWithOrderedDetails,
    setItemNosAndTypeWithOrderedDetails,
  ] = useState<ItemNoAndTypeWithPTagsOrdered[] | null | Loading>(
    initialItemNosAndTypeWithOrderedDetails
  );

  const { items, isFetchingItems } = useAllAttachedItems(
    itemNosAndTypeWithOrderedDetails ?? LOADING
  );

  const handlePrintPtagsQuantityChange = (
    itemNo: string,
    newPrintPtagsQuantity: number
  ) => {
    setItemNosAndTypeWithOrderedDetails((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) =>
        item.no === itemNo
          ? { ...item, ptags_ordered: newPrintPtagsQuantity }
          : item
      );
    });
  };

  const handleConfirm = () => {
    if (Array.isArray(itemNosAndTypeWithOrderedDetails)) {
      onTaskChange({ details: { items: itemNosAndTypeWithOrderedDetails } });
    }
  };

  if (!shouldShowField(task, visibility)) {
    return null;
  }

  if (isFetchingItems || isLoading(itemNosAndTypeWithOrderedDetails)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <ArticlesPrintPtagsQuantity
      itemNosAndTypeWithOrderedDetails={itemNosAndTypeWithOrderedDetails}
      items={items}
      onPrintPtagsQuantityChange={handlePrintPtagsQuantityChange}
      handleConfirm={handleConfirm}
    />
  );
};

export default ArticlesPrintPtagsCreatorFragment;
